import type { ColumnDef } from '@tanstack/react-table';
import cx from 'clsx';
import { GET, isUUID, truncate } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { Alert } from 'types/Alert.ts';
import type { Shipment } from 'types/Shipment.ts';
import type { ShipmentHistoryItem } from 'types/ShipmentHistoryItem.ts';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { AlertTag } from './AlertTag.tsx';
import { Datefrom } from './Datefrom.tsx';
import { Table } from './table/index.ts';

type Props = {
  shipment: Shipment;
  onClose?: () => void;
  isOpen: boolean;
};

export const ShipmentHistory: React.FC<Props> = ({ shipment, onClose, isOpen }) => {
  const { t } = useTranslation();
  const swr = useSWR([`/shipment/${shipment.id}/history`], GET);

  const columns = React.useMemo<ColumnDef<ShipmentHistoryItem>[]>(
    () => [
      {
        header: t('shipmentHistory.columns.datetime'),
        accessorKey: 'datetime',
        meta: { className: 'justify-left' },
        minSize: 180,
        cell: (info) => {
          const value = info.getValue<string>();
          return <Datefrom blink={false} date={value} aliveInHours={1} />;
        },
      },
      {
        header: t('shipmentHistory.columns.type'),
        accessorKey: 'type',
        meta: { className: 'justify-left' },
        minSize: 100,
        cell: (info) => {
          const value = info.getValue<ShipmentHistoryItem['type']>();
          return (
            <Tag className={cx(value === 'activity' ? 'blue' : 'red', '!p-2 text-sm')}>
              {value === 'activity' ? t('shipmentHistory.types.activity') : t('shipmentHistory.types.alert')}
            </Tag>
          );
        },
      },
      {
        header: t('shipmentHistory.columns.event'),
        accessorKey: 'event',
        meta: { className: 'justify-left' },
        minSize: 250,
        cell: (info) => {
          if (info.row.original.type === 'alert') return <AlertTag alert={info.row.original.alert as Alert} />;
          const value = info.getValue<string>();
          const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
          return info.row.original.description ? <Tooltip text={info.row.original.description}>{capitalized}</Tooltip> : capitalized;
        },
      },
      {
        header: t('shipmentHistory.columns.resource'),
        accessorKey: 'resource_type',
        meta: { className: 'justify-left' },
        minSize: 100,
      },
      {
        header: t('shipmentHistory.columns.resourceId'),
        accessorKey: 'resource_id',
        meta: { className: 'justify-left' },
        minSize: 100,
        cell: (info) => {
          const value = info.getValue<string>();
          if (!value) return null;
          return (
            <CopyableText copyText={value} className="font-mono">
              {isUUID(value) ? truncate(value, 8, '') : value}
            </CopyableText>
          );
        },
      },
      {
        header: t('shipmentHistory.columns.generatedFrom'),
        accessorKey: 'from',
        meta: { className: 'justify-left' },
        minSize: 180,
        cell: (info) => {
          const value = info.getValue<string>();
          if (!value) return null;
          return isUUID(value) ? (
            <CopyableText copyText={value} className="font-mono">
              {isUUID(value) ? truncate(value, 8, '') : value}
            </CopyableText>
          ) : (
            <span>{value}</span>
          );
        },
      },
    ],
    [t],
  );

  return (
    <Modal title={<h4>{t('shipmentHistory.title')}</h4>} key={shipment.id} isOpen={isOpen} onClose={onClose}>
      <div className="3 flex w-[900px] flex-col">
        <Table
          className={cx('max-h-[calc(100vh-370px)]')}
          columns={columns}
          swr={swr}
          rowsCount={swr.data?.length}
          defaultSorting={[{ id: 'datetime', desc: true }]}
          estimateSize={50}
          overscan={50}
        />
      </div>
    </Modal>
  );
};
